/**
 * ForgotPassword — `/forgot-password`
 *
 * Single email field. We POST to /auth/recovery/request which always
 * returns success (anti-enumeration). After submit we show a
 * confirmation card identical in tone to the magic-link "Link sent"
 * state so the UX is consistent.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, CheckCircle2, ArrowRight, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import SovereigntyBanner from '@/components/SovereigntyBanner';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post('/auth/recovery/request', {
        email: email.trim().toLowerCase(),
        redirect_url: window.location.origin,
      });
      setSent(true);
      if (data?.debug_link) {
        toast('Local-dev mode: opening recovery link in 1s', { description: data.debug_link });
        setTimeout(() => { window.location.href = data.debug_link; }, 1000);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send recovery link');
    } finally { setBusy(false); }
  };

  return (
    <main
      data-testid="forgot-password-page"
      className="relative min-h-screen overflow-hidden text-white flex items-center justify-center"
      style={{
        background:
          'radial-gradient(1200px 600px at 80% -10%, rgba(174,82,234,0.12), transparent 60%),' +
          'radial-gradient(900px 600px at -10% 110%, rgba(174,82,234,0.10), transparent 60%),' +
          'linear-gradient(180deg, #050505 0%, #0A0A0A 50%, #050505 100%)',
      }}
    >
      <div className="relative rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-7 lg:p-9 shadow-[0_30px_120px_-40px_rgba(174,82,234,0.4)] max-w-md w-full mx-4">
        <div
          className="h-12 w-12 rounded-md flex items-center justify-center border"
          style={{
            backgroundColor: 'rgba(174,82,234,0.10)',
            color: '#AE52EA',
            borderColor: 'rgba(174,82,234,0.40)',
          }}
        >
          <KeyRound size={22} />
        </div>
        <div className="mt-4 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
          [ACCOUNT · RECOVERY]
        </div>
        <h1 className="display text-3xl mt-1.5">
          {sent ? 'Check your inbox.' : 'Forgot your password?'}
        </h1>
        <p className="text-xs text-white/55 mt-2">
          {sent
            ? `If an account exists for ${email}, we just sent a one-tap reset link. It's valid for 15 minutes and can only be used once.`
            : 'Enter your email and we\'ll send a secure link to reset your password. No support ticket required.'}
        </p>

        {!sent && <SovereigntyBanner size="sm" className="mt-5 w-full" />}

        {!sent ? (
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="forgot-password-form">
            <label className="block text-[10px] uppercase tracking-[0.22em] text-white/45">
              Email
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="forgot-password-email"
                autoComplete="email"
                placeholder="you@company.com"
                className="mt-1.5 w-full rounded-md border border-white/15 bg-black/40 px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#8B3FC5]/30 focus:border-[#8B3FC5] normal-case tracking-normal"
              />
            </label>
            <button
              type="submit"
              disabled={busy || !email.trim()}
              data-testid="forgot-password-submit"
              className="group relative w-full inline-flex items-center justify-center gap-2 rounded-md bg-[#8B3FC5] text-white text-sm font-semibold px-4 py-2.5 transition-all hover:bg-[#AE52EA] hover:shadow-[0_0_36px_-4px_rgba(174,82,234,0.7)] disabled:opacity-60"
            >
              {busy ? 'Sending link…' : 'Email me a reset link'}
              <Mail size={14} className="transition-transform group-hover:translate-x-0.5" />
            </button>
          </form>
        ) : (
          <div
            className="mt-6 rounded-md border border-emerald-500/40 bg-emerald-500/[0.08] p-4"
            data-testid="forgot-password-sent"
          >
            <div className="flex items-center gap-2 text-emerald-400">
              <CheckCircle2 size={16} />
              <span className="text-[10px] uppercase tracking-[0.22em] font-bold">Link sent</span>
            </div>
            <p className="mt-3 text-[11px] text-white/55 leading-relaxed">
              The link will expire in 15 minutes. If you don&apos;t see it
              within a minute, check spam, then come back and request another.
            </p>
            <button
              type="button"
              onClick={() => setSent(false)}
              data-testid="forgot-password-resend"
              className="mt-3 text-[10px] uppercase tracking-[0.22em] font-bold text-[#AE52EA] hover:text-white"
            >
              Send a different one →
            </button>
          </div>
        )}

        <div className="mt-7 flex items-center justify-between gap-3">
          <Link to="/login" className="text-xs text-white/55 hover:text-white">
            ← Back to sign in
          </Link>
          <Link to="/signup" className="text-xs text-[#8B3FC5] font-medium hover:underline inline-flex items-center gap-1">
            Create an account
            <ArrowRight size={11} />
          </Link>
        </div>
      </div>
    </main>
  );
}
